import { TUNABLES } from "../config";
import { onLanguageChange, phrases } from "../i18n";
import { make, makeButton } from "./dom";

/**
 * B3.10, the plan's end screen and "a bug button on every screen": the short prompt after a
 * match and the bug report, in plain DOM over the game (no Pixi). Both only send through the
 * page's recorder, and only where the feedback switch is on (`switchOf("feedback", ...)`):
 * what a player writes is data the privacy note describes since the owner approved it (N35).
 */

export type Rating = "good" | "ok" | "bad";
export type Screen = "front" | "match" | "end";

/** What a bug report says of the game on screen. */
export interface MatchContext {
  readonly game: string;
  readonly tick: number;
  readonly screen: Screen;
}

/** Whether the prompt comes after the `ended`-th match a tab ends: the second, then every other. */
export function promptDue(ended: number): boolean {
  const { firstAfter, every } = TUNABLES.feedback;
  return ended >= firstAfter && (ended - firstAfter) % every === 0;
}

let matchContext: (() => MatchContext) | undefined;

/** The game registers what a bug report reads; the front page has none. */
export function setMatchContext(read: () => MatchContext): void {
  matchContext = read;
}

/** The bug report's context: the game's, or the front page's. */
function currentContext(): MatchContext {
  return matchContext?.() ?? { game: "", tick: 0, screen: "front" };
}

/** A line the game's keys never see while the player types in it. */
function writing<T extends HTMLInputElement | HTMLTextAreaElement>(
  field: T,
  placeholder: string,
): T {
  field.placeholder = placeholder;
  field.maxLength = TUNABLES.events.maxFeedbackChars;
  field.addEventListener("keydown", (event) => event.stopPropagation());
  return field;
}

const THANKS_MS = 1600;

/**
 * The prompt after a match: "How was that match?", one tap on a rating sends it with the optional
 * line. Skip closes it; it never covers or blocks Play again. Returns a close for the next run.
 */
export function showFeedbackPrompt(options: {
  readonly send: (rating: Rating, text: string) => void;
}): () => void {
  document.getElementById("feedback-prompt")?.remove();
  const words = phrases().front.feedback;
  const panel = make("section", { id: "feedback-prompt" });
  panel.setAttribute("aria-label", words.label);
  const title = make("p", { className: "feedback-title", text: words.question });
  const line = writing(make("input"), words.why);
  line.type = "text";
  line.id = "feedback-line";
  const ratings = make("div", { className: "feedback-ratings" });
  const close = () => panel.remove();
  for (const rating of ["good", "ok", "bad"] as const) {
    const button = makeButton({ text: words.ratings[rating] });
    button.dataset.rating = rating;
    button.addEventListener("click", () => {
      options.send(rating, line.value.trim());
      panel.replaceChildren(make("p", { className: "feedback-title", text: words.thanks }));
      setTimeout(close, THANKS_MS);
    });
    ratings.append(button);
  }
  const skip = makeButton({ className: "feedback-skip", text: words.skip });
  skip.addEventListener("click", close);
  ratings.append(skip);
  panel.append(title, line, ratings);
  document.body.append(panel);
  return close;
}

/**
 * The bug button, on every screen: it opens a box for what went wrong; Send passes the text and
 * the context of the game on screen, Cancel or Escape closes it. Its words follow the language.
 */
export function mountBugButton(options: {
  readonly send: (text: string, context: MatchContext) => void;
}): void {
  if (document.getElementById("bug-button")) {
    return;
  }
  const button = makeButton({ id: "bug-button" });
  const form = make("section", { id: "bug-form" });
  form.hidden = true;
  const title = make("p", { className: "feedback-title" });
  const box = writing(make("textarea"), "");
  box.id = "bug-text";
  box.rows = 3;
  const row = make("div", { className: "feedback-ratings" });
  const sendButton = makeButton({ id: "bug-send" });
  const cancel = makeButton({ className: "feedback-skip" });
  row.append(sendButton, cancel);
  form.append(title, box, row);
  const speak = (): void => {
    const words = phrases().front.feedback;
    button.textContent = words.bug;
    button.title = words.reportBug;
    form.setAttribute("aria-label", words.reportBug);
    title.textContent = words.whatWentWrong;
    box.placeholder = words.whatYouSaw;
    sendButton.textContent = words.send;
    cancel.textContent = words.cancel;
  };
  speak();
  onLanguageChange(speak);

  const hide = () => {
    form.hidden = true;
    form.replaceChildren(title, box, row);
  };
  button.addEventListener("click", () => {
    form.hidden = !form.hidden;
    if (!form.hidden) {
      box.focus();
    }
  });
  cancel.addEventListener("click", hide);
  form.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      hide();
    }
  });
  sendButton.addEventListener("click", () => {
    const text = box.value.trim();
    if (!text) {
      box.focus();
      return;
    }
    options.send(text, currentContext());
    box.value = "";
    form.replaceChildren(
      make("p", { className: "feedback-title", text: phrases().front.feedback.sent }),
    );
    setTimeout(hide, THANKS_MS);
  });
  document.body.append(button, form);
}
